// Add guard at the very top
if (typeof window.Shopify === 'undefined') {
  window.Shopify = {};
}

if (typeof Shopify.CollectionFilters === 'undefined' || typeof Shopify.CollectionFiltersInitialized === 'undefined') {

  // Assuming Motion is available globally, otherwise import { animate } from "motion"

  class CollectionFilters {
    constructor(container) {
      console.log('CollectionFilters: Initializing...');
      // Check if Motion.animate is available (Capital M)
      if (typeof Motion === 'undefined' || typeof Motion.animate === 'undefined') {
        console.error('CollectionFilters: Motion One (Motion.animate) not found. Animations will not work.');
      }
      this.container = container;
      if (!this.container) {
        console.error('CollectionFilters: Container element not found.');
        return;
      }

      this.filterForm = this.container.querySelector('#CollectionFiltersForm');
      this.productGridContainer = document.getElementById('ProductGridContainer');
      this.clearAllButton = this.container.querySelector('.collection-filters__clear-all');
      this.detailsElement = this.container.closest('.collection-filters__main-details'); // Find the parent <details>
      this.triggerElement = this.detailsElement?.querySelector('.collection-filters__trigger'); // Get the summary/trigger
      this.contentElement = this.container; // The container passed is the content to animate
      this.sortButton = this.container.querySelector('.facet-filters__sort-button');
      this.sortDropdown = this.container.querySelector('#SortBy-options');
      this.hiddenSortSelect = this.container.querySelector('#SortBy');
      this.sortButtonText = this.sortButton?.querySelector('.facet-filters__sort-button-text');
      
      // Debounce timer
      this.debounce = null;

      console.log('Elements:', { 
        container: this.container, 
        details: this.detailsElement, 
        trigger: this.triggerElement, 
        content: this.contentElement 
      });

      if (!this.detailsElement || !this.triggerElement || !this.contentElement) {
        console.error('CollectionFilters: Accordion elements (details, trigger, content) not found.');
        return; // Need these for animation
      }
      
      // Ensure content is hidden initially if details is not open
      if (!this.detailsElement.open) {
        console.log('Accordion starting closed, hiding content.');
        this.contentElement.style.display = 'none'; // Start hidden for JS control
        this.contentElement.style.opacity = 0;
      } else {
        console.log('Accordion starting open.');
      }

      if (!this.sortButton || !this.sortDropdown || !this.hiddenSortSelect || !this.sortButtonText) {
        console.warn('CollectionFilters: Custom sort elements not found. Sort functionality might be limited.');
      }

      this.initListeners();
      this.populateSortDropdown(); // Populate dropdown on init
    }

    initListeners() {
      if (!this.filterForm || !this.triggerElement) {
        console.error('CollectionFilters: Filter form or trigger element not found for listeners.');
        return;
      }

      // Accordion Trigger Listener
      this.triggerElement.addEventListener('click', this.onAccordionTriggerClick.bind(this));

      // Listen for filter input changes (excluding sort select)
      this.filterForm.addEventListener('input', this.onFormInput.bind(this));

      // Handle custom sort button click
      if (this.sortButton) {
        this.sortButton.addEventListener('click', (event) => {
          event.preventDefault(); // Prevent default button behavior (like form submission)
          event.stopPropagation(); // Stop event from bubbling up
          this.toggleSortDropdown();
        });
      }
      
      // Add listener for clicks within the sort list UL (delegated)
      if (this.sortDropdown) {
        this.sortDropdown.addEventListener('click', this.onSortLabelClick.bind(this));
      }

      if (this.clearAllButton) {
        this.clearAllButton.addEventListener('click', this.clearFilters.bind(this));
      }
    }
    
    populateSortDropdown() {
      if (!this.hiddenSortSelect || !this.sortDropdown) return;

      this.sortDropdown.innerHTML = ''; // Clear existing options
      const currentSortValue = this.hiddenSortSelect.value;
      const formId = this.filterForm?.id || 'sort-by'; // Fallback id

      Array.from(this.hiddenSortSelect.options).forEach((option, index) => {
        const listItem = document.createElement('li');
        // Use similar classes as filter items for consistent styling
        listItem.className = 'list-menu__item facets__item'; 

        const label = document.createElement('label');
        label.className = 'facet-radio__label'; 
        label.htmlFor = `${formId}-SortBy-${index}`;

        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = `sort_by_${formId}`; 
        radio.id = `${formId}-SortBy-${index}`;
        radio.value = option.value;
        radio.className = 'visually-hidden facet-radio__input'; // Hide radio
        if (option.value === currentSortValue) {
          radio.checked = true;
        }

        const customRadioBox = document.createElement('span');
        customRadioBox.className = 'facet-radio__custom-box'; 
        customRadioBox.setAttribute('aria-hidden', 'true');

        const textSpan = document.createElement('span');
        textSpan.className = 'facet-radio__text'; 
        textSpan.textContent = option.textContent;

        label.appendChild(radio);
        label.appendChild(customRadioBox);
        label.appendChild(textSpan);
        listItem.appendChild(label);
        this.sortDropdown.appendChild(listItem);
      });
      
      // Ensure button text is correct on load
      if (this.sortButtonText && this.hiddenSortSelect.selectedIndex >= 0) {
        this.sortButtonText.textContent = this.hiddenSortSelect.options[this.hiddenSortSelect.selectedIndex].textContent;
      }
    }

    toggleSortDropdown() {
      if (!this.sortDropdown || !this.sortButton) return;
      const isExpanded = this.sortButton.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        this.closeSortDropdown();
      } else {
        this.sortDropdown.hidden = false;
        this.sortButton.setAttribute('aria-expanded', 'true');
        // Focus the label of the checked radio or the first label
        const checkedRadio = this.sortDropdown.querySelector('input[type="radio"]:checked');
        const focusTarget = checkedRadio ? checkedRadio.closest('label') : this.sortDropdown.querySelector('.facet-radio__label');
        focusTarget?.focus();
      }
    }

    closeSortDropdown() {
      if (!this.sortDropdown || !this.sortButton) return;
      if (this.sortButton.getAttribute('aria-expanded') === 'true') {
        this.sortDropdown.hidden = true;
        this.sortButton.setAttribute('aria-expanded', 'false');
      }
    }

    onSortLabelClick(event) {
      const label = event.target.closest('.facet-radio__label');
      if (!label) return; 

      const radio = label.querySelector('input[type="radio"]');
      // Check if click is on radio itself or if radio is already checked
      if (!radio || radio === event.target || radio.checked) return; 

      const newValue = radio.value;
      const newText = label.querySelector('.facet-radio__text')?.textContent;

      if (!this.hiddenSortSelect || !this.sortButtonText || !newText) return;

      // Manually set checked = true FOR the clicked radio 
      // (browser might not do this automatically if click was on label and radio hidden)
      radio.checked = true; 

      // Update hidden select value
      this.hiddenSortSelect.value = newValue;

      // Update button text
      this.sortButtonText.textContent = newText;

      // Optional: Update visual styles immediately if needed, though `checked` state should handle it

      this.closeSortDropdown();
      this.renderProductGrid(); 
    }
    
    onAccordionTriggerClick(event) {
      event.preventDefault(); // Stop default details toggle
      console.log(`Trigger clicked. Details open state: ${this.detailsElement.open}`);
      if (this.detailsElement.open) {
        console.log('Calling closeAccordion...');
        this.closeAccordion();
      } else {
        console.log('Calling openAccordion...');
        this.openAccordion();
      }
    }

    openAccordion() {
      if (!this.detailsElement || !this.contentElement) return;
      console.log('openAccordion: Setting display=\'\' and details.open=true');
      // Set overflow hidden before animating height/max-height
      this.contentElement.style.overflow = 'hidden'; 
      this.contentElement.style.display = ''; 
      this.detailsElement.open = true; 

      console.log('openAccordion: Starting max-height animation...');
      Motion.animate(
          this.contentElement,
          // Animate max-height to a large value with units
          { maxHeight: [0, '1500px'], opacity: [0, 1] }, 
          { duration: 0.5, easing: "ease-out" } // Slower duration, ease-out
      ).finished.then(() => {
          console.log('openAccordion: Animation finished.');
          // After opening, allow content overflow if necessary
          this.contentElement.style.overflow = ''; 
      });
    }

    closeAccordion() {
      if (!this.detailsElement || !this.contentElement) return;
      console.log('closeAccordion: Starting max-height animation...');
      // Ensure overflow is hidden for closing animation
      this.contentElement.style.overflow = 'hidden'; 
      Motion.animate(
          this.contentElement,
          { maxHeight: 0, opacity: 0 },
          { duration: 0.3, easing: "ease-in-out" } // Faster duration
      ).finished.then(() => {
          console.log('closeAccordion: Animation finished. Setting details.open=false and display=none');
          this.detailsElement.open = false; 
          this.contentElement.style.display = 'none'; 
          this.closeSortDropdown(); 
      });
    }

    onFormInput(event) {
      if (event.target === this.hiddenSortSelect) return;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.renderProductGrid();
      }, 500);
    }

    renderProductGrid() {
      if (!this.filterForm || !this.productGridContainer) {
        console.error('CollectionFilters: Filter form or product grid container not found for rendering.');
        return;
      }

      const formData = new FormData(this.filterForm);
      const searchParams = new URLSearchParams(formData).toString();

      // Get section ID from the inner #product-grid element within the container
      const productGridElement = this.productGridContainer.querySelector('#product-grid');
      const sectionId = productGridElement?.dataset.id; 

      if (!sectionId) {
        console.error('CollectionFilters: Could not determine section ID from #product-grid element within ProductGridContainer.');
        return;
      }

      const baseUrl = window.location.pathname;
      // Construct URL for fetching section render
      const url = `${baseUrl}?section_id=${sectionId}&${searchParams}`;

      this.showLoading();

      fetch(url)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.text();
        })
        .then(responseText => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newGridContent = html.getElementById('product-grid');
          const newFiltersContent = html.getElementById('CollectionFiltersForm'); // Target form for counts
          const newPagination = html.querySelector('.pagination-wrapper');
          
          if (!newGridContent) {
            console.error('CollectionFilters: #product-grid not found in fetched response.');
            return;
          }
          
          const paginationElement = this.productGridContainer.querySelector('.pagination-wrapper');

          if (productGridElement) {
            productGridElement.innerHTML = newGridContent.innerHTML;
          } else {
            console.error('CollectionFilters: Existing #product-grid not found in container.');
          }

          // Update Pagination
          if (newPagination && paginationElement) {
            paginationElement.innerHTML = newPagination.innerHTML;
          } else if (newPagination && !paginationElement) {
            this.productGridContainer.appendChild(newPagination.cloneNode(true));
          } else if (!newPagination && paginationElement) {
            paginationElement.remove();
          }
          
          // Update filter counts
          if (newFiltersContent) {
            this.updateFilterCounts(newFiltersContent);
          }

          // Update URL
          window.history.pushState({ searchParams }, '', `${baseUrl}?${searchParams}`);
        })
        .catch(e => {
          console.error('CollectionFilters: Error fetching or processing filter results:', e);
        })
        .finally(() => {
          this.hideLoading();
        });
    }
    
    updateFilterCounts(newFiltersElement) {
      const currentFilterItems = this.filterForm.querySelectorAll('.facets__item, .facet-filter-group--price_range .price-range'); // Include price range if needed
      const newFilterItems = newFiltersElement.querySelectorAll('.facets__item, .facet-filter-group--price_range .price-range');
    
      const newCountsMap = new Map();
      newFilterItems.forEach(item => {
        const checkbox = item.querySelector('input[type="checkbox"]');
        const rangeMin = item.querySelector('input[name*="filter.v.price.gte"]');
        
        if (checkbox) {
          const countElement = item.querySelector('.facet-checkbox__text');
          const countMatch = countElement?.textContent.match(/\((\d+)\)/);
          if (countMatch) {
            newCountsMap.set(checkbox.value, { count: parseInt(countMatch[1], 10), label: countElement.textContent });
          }
        } 
        // Add logic for price range counts if available/needed
        // else if (rangeMin) { ... }
      });
    
      currentFilterItems.forEach(item => {
        const checkbox = item.querySelector('input[type="checkbox"]');
        const rangeMin = item.querySelector('input[name*="filter.v.price.gte"]');
        
        if (checkbox) {
          const labelElement = item.querySelector('.facet-checkbox');
          const textElement = item.querySelector('.facet-checkbox__text');
          if (labelElement && textElement) {
            const newData = newCountsMap.get(checkbox.value);
            if (newData !== undefined) {
              textElement.textContent = newData.label;
              if (newData.count === 0 && !checkbox.checked) {
                labelElement.classList.add('facet-checkbox--disabled');
                checkbox.disabled = true;
              } else {
                labelElement.classList.remove('facet-checkbox--disabled');
                checkbox.disabled = false;
              }
            } else {
              labelElement.classList.add('facet-checkbox--disabled');
              checkbox.disabled = true;
              const currentText = textElement.textContent.replace(/\(\d+\)/, '(0)');
              textElement.textContent = currentText;
            }
          }
        }
        // Add logic for price range counts if available/needed
        // else if (rangeMin) { ... }
      });
    }

    clearFilters() {
      if (!this.filterForm) return;
      this.filterForm.reset();
      
      // Manually set the sort select back to the default option 
      const defaultSortOption = this.hiddenSortSelect?.options[0];
      if (defaultSortOption && this.hiddenSortSelect) {
        this.hiddenSortSelect.value = defaultSortOption.value;
        // Update button text
        if(this.sortButtonText) {
          this.sortButtonText.textContent = defaultSortOption.textContent;
        }
         // Update aria-selected in custom dropdown
         this.populateSortDropdown(); // Re-populates and sets correct aria-selected
      }

      // Prevent renderProductGrid from firing immediately due to reset() triggering input event
      clearTimeout(this.debounce);
      this.renderProductGrid();
    }

    showLoading() {
      if (this.productGridContainer) {
        this.productGridContainer.classList.add('loading');
      }
      if (this.filterForm) {
        // Disable form interaction while loading
         Array.from(this.filterForm.elements).forEach(el => el.disabled = true);
      }
    }

    hideLoading() {
      if (this.productGridContainer) {
        this.productGridContainer.classList.remove('loading');
      }
       if (this.filterForm) {
         // Re-enable form interaction
         Array.from(this.filterForm.elements).forEach(el => {
           // Re-enable only if it wasn't already disabled by count=0
           const label = el.closest('.facet-checkbox');
           if (!label || !label.classList.contains('facet-checkbox--disabled') || el.type !== 'checkbox') {
             el.disabled = false;
           }
         });
      }
    }
  } // End of CollectionFilters class
  
  // Instantiate the class for the filters found on the page
  document.addEventListener('DOMContentLoaded', () => {
    const filterContainer = document.getElementById('CollectionFiltersContainer');
    if (filterContainer) {
      // Ensure we don't re-instantiate if it somehow runs again due to AJAX content loads
      // that might re-trigger DOMContentLoaded for new script tags without a proper guard.
      if (!Shopify.CollectionFiltersInstance) {
         Shopify.CollectionFiltersInstance = new CollectionFilters(filterContainer);
         console.log('CollectionFilters: Instantiated via DOMContentLoaded.');
      } else {
        console.log('CollectionFilters: Instance already exists, not re-instantiating via DOMContentLoaded.');
      }
    }
  }); 

  Shopify.CollectionFilters = CollectionFilters; // Assign the class to the Shopify global object
  Shopify.CollectionFiltersInitialized = true; 
  console.log('CollectionFilters: Script defined and guard flag Shopify.CollectionFiltersInitialized set to true.');

} else {
  console.log('CollectionFilters: Script already declared/initialized (Shopify.CollectionFilters or Shopify.CollectionFiltersInitialized is true), skipping re-run.');
} 